import React, { Component, Fragment } from 'react';
import axios from 'axios';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { hostServer, isProd } from '../../../utilities/UrlPaths';
import { errorLoadingCatalogText } from '../../../utilities/StaticText';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import getPackageID from '../diy-services/getPackageId';
import Step3Dropzone from './Step3Dropzone';

// This represents the un-uploaded state of step3
class Step3PreUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      hasUploadErrors: false,
      loading: true,
      selectedRadio: 'artfact'
    };
  }

  async componentDidMount() {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    const packageID = await getPackageID(catalogID);

    if (packageID) {
      this.setState({ packageID, loading: false });
    } else {
      this.setState({ loading: false, error: true });
    }
  }

  // Download a spreadsheet model based on the auction type and format selected
  downloadModel = format => {
    const {
      match: {
        params: { type }
      }
    } = this.props;

    // Just in case the user does something silly, fall back onto the live permutation
    const auctionType = type.toLowerCase() === 'timed' ? 'timed' : 'live';
    let fileName = '';
    if (format === 'standard') {
      fileName = (auctionType === 'timed') ? '/06/Standard-Catalog-Model-Timed-4.xls' : '/06/Live-Standard-Catalog-Model.xls';
    } else {
      fileName = `/07/${auctionType}-auctionflex.csv`;
    }

    const modelUrl = `${hostServer}/inv/help-center/wp-content/uploads/sites/57/2020${fileName}`;
    window.location.href = modelUrl;
  };

  downloadSnapshot = () => {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    window.location.href = `/cps/app/snapshot/${catalogID}/lot-data?version=v2`;
  };

  handleRadioChange = e => {
    this.setState({
      selectedRadio: e.target.id
    });
  };

  setUploadErrors = hasUploadErrors => {
    this.setState({ hasUploadErrors });
  };

  /**
   * The user decided they wanted to add items individually - set step tracker to 6
   * and take them to the add lot page
   */
  skipToSummary = async () => {
    const {
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    await axios.post(`/cps/app/diy-step-tracker/${catalogID}`, 6, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });

    this.props.history.push(`/session/${type}/${groupID}/${catalogID}/lot/0`);
  };

  render() {
    const { error, loading } = this.state;

    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    if (error) {
      return <InlineAlert baseText={errorLoadingCatalogText} />;
    }

    const {
      handleUpload,
      location: { state },
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;
    const { hasUploadErrors, packageID, selectedRadio } = this.state;

    const isEditingSession = get(state, 'linkedFrom', null) === 'step6';

    return (
      <div className="mt-4">
        <Row>
          {/* Start Left Column */}
          <Col xs={8}>
            {isEditingSession ? (
              <Fragment>
                <h3 className="text-transform-unset">
                  To edit multiple items that you&apos;ve already uploaded, follow the
                  three easy steps below.
                </h3>
                <p>
                  If you are simply adding new items, skip to the last step and upload the
                  spreadsheet from your computer.
                </p>
                <p className="mb-0">
                  1.
                  <Button color="link" onClick={() => this.downloadSnapshot()}>
                    &nbsp;Download a spreadsheet snapshot
                  </Button>
                  &nbsp;of your current catalog.
                </p>
                <p className="mb-0">
                  2. Edit your items in Excel. New items will be added, existing
                  items don&apos;t need to be re-included if they are not being changed.
                </p>
                <p>3. Upload your spreadsheet:</p>
              </Fragment>
            ) : (
              <Fragment>
                <h3>Upload Items</h3>
                <p>
                  Drag &amp; drop the items spreadsheet in the uploader box below (or click the
                  &quot;choose a file from your computer&quot; link to find your file)
                </p>
              </Fragment>
            )}

            <p>
              You may also upload items individually by choosing &nbsp;
              <Button color="link" role="link" onClick={() => this.skipToSummary()}>
                <FontAwesomeIcon icon="plus" />
                &nbsp;Add single item
              </Button>
            </p>

            <h3>Select Your File Format</h3>
            <p>
              If you are unsure which format to use, click the examples on the right side of this
              page.
            </p>

            <FormGroup check>
              <Label for="artfact" check>
                <Input
                  id="artfact"
                  type="radio"
                  name="fileFormat"
                  checked={selectedRadio === 'artfact'}
                  onChange={e => this.handleRadioChange(e)}
                />
                Standard
              </Label>
            </FormGroup>
            <FormGroup check className="my-2">
              <Label for="auctionflex" check>
                <Input
                  id="auctionflex"
                  type="radio"
                  name="fileFormat"
                  checked={selectedRadio === 'auctionflex'}
                  onChange={e => this.handleRadioChange(e)}
                />
                Auction Flex
              </Label>
            </FormGroup>

            <Step3Dropzone
              acceptedFileList=".csv,.xls,.xlsx"
              catalogID={catalogID}
              handleUpload={handleUpload}
              setUploadErrors={this.setUploadErrors}
              multiple={false}
              packageID={packageID}
              selectedRadio={selectedRadio}
            />
          </Col>

          {/* Start Right Column */}
          <Col xs={4}>
            <h3>Spreadsheet Requirements</h3>
            <p className="mb-0">
              Download the relevant spreadsheet model to your preferred spreadsheet application:
            </p>
            <Row className="pt-2">
              <Col>
                <Button color="link" onClick={() => this.downloadModel('standard')}>
                  Standard Model
                </Button>
              </Col>
            </Row>
            <Row className="pb-2">
              <Col>
                <Button color="link" onClick={() => this.downloadModel('auctionflex')}>
                  Auction Flex Model
                </Button>
              </Col>
            </Row>
            <p>Enter your item details to the template and save the spreadsheet.</p>
            <p className="mb-0">For more tips and tricks, please read our</p>
            <a
              rel="noopener noreferrer"
              target="_blank"
              href={`${hostServer}/inv/help-center/resource/catalog/preparing-your-catalog-spreadsheet/`}
            >
              upload tip sheet
            </a>
            .
          </Col>
        </Row>

        {!hasUploadErrors && (
          <div className="mt-4">
            {isEditingSession ? (
              <Link to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/6`}>
                Cancel &amp; Return to Summary
              </Link>
            ) : (
              <Link to="/my-auctions/current">Save &amp; Exit</Link>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(Step3PreUpload);
